import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Lock, Plug, Eye, EyeOff, CheckCircle, AlertCircle, 
  FileSignature, Shield, Loader2, BarChart 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CRMLayout from '../../components/CRM/CRMLayout';
import { useAuth } from '../../components/CRM/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Settings() {
  const { user } = useAuth();

  return (
    <CRMLayout>
      <div className="pb-20">
        <div className="bg-navy-800 border-b border-white/5">
          <div className="px-6 py-6">
            <h1 className="text-3xl font-heading text-white uppercase tracking-wide" data-testid="settings-title">
              Settings
            </h1>
            <p className="text-gray-400 mt-1">Manage your account and integrations</p>
          </div>
        </div>

        <div className="px-6 py-8 max-w-3xl space-y-8">
          <AccountSection username={user?.username} />
          <PasswordSection />
          <IntegrationsSection />
        </div>
      </div>
    </CRMLayout>
  );
}

function AccountSection({ username }) {
  return (
    <div className="bg-navy-800 rounded-xl border border-white/5 p-6" data-testid="account-section">
      <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <Shield className="h-5 w-5 text-purple-400" /> Account
      </h2>
      <div>
        <label className="text-gray-400 text-sm">Email / Username</label>
        <p className="text-white mt-1">{username}</p>
      </div>
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/flashiq/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-navy-800 rounded-xl border border-white/5 p-6" data-testid="password-section">
      <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <Lock className="h-5 w-5 text-purple-400" /> Change Password
      </h2>
      <form onSubmit={handleChangePassword} className="space-y-4">
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="current-password-input"
              className="w-full px-4 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 pr-10"
              placeholder="Enter current password"
              required
            />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
              className="w-full px-4 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 pr-10"
              placeholder="Enter new password"
              required
            />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="confirm-password-input"
            className="w-full px-4 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            placeholder="Confirm new password"
            required
          />
          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
          )}
        </div>
        <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="change-password-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Update Password
        </Button>
      </form>
    </div>
  );
}

function IntegrationsSection() {
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const res = await axios.get(`${API}/flashiq/integrations`);
      setIntegrations(res.data);
    } catch (err) {
      console.error('Error fetching integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveIntegration = async (key, data) => {
    try {
      const updated = { ...integrations, [key]: { ...integrations[key], ...data } };
      await axios.put(`${API}/flashiq/integrations`, updated);
      setIntegrations(updated);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} settings saved`);
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="integrations-section">
      <h2 className="text-white font-semibold text-lg flex items-center gap-2">
        <Plug className="h-5 w-5 text-purple-400" /> Integrations
      </h2>

      {/* DocuSign */}
      <IntegrationCard
        name="DocuSign"
        description="Send and manage e-signatures for loan documents directly from FlashIQ"
        icon={<FileSignature className="h-6 w-6" />}
        color="blue"
        connected={integrations?.docusign?.connected}
        fields={[
          { key: 'client_id', label: 'Integration Key (Client ID)', value: integrations?.docusign?.client_id || '' },
          { key: 'account_id', label: 'Account ID', value: integrations?.docusign?.account_id || '' }
        ]}
        onSave={(data) => saveIntegration('docusign', data)}
        testIdPrefix="docusign"
      />

      {/* Equifax */}
      <IntegrationCard
        name="Equifax"
        description="Run credit checks and verify applicant identity from within deal workflows"
        icon={<Shield className="h-6 w-6" />}
        color="green"
        connected={integrations?.equifax?.connected}
        fields={[
          { key: 'api_key', label: 'API Key', value: integrations?.equifax?.api_key || '' },
          { key: 'subscriber_id', label: 'Subscriber ID', value: integrations?.equifax?.subscriber_id || '' }
        ]}
        onSave={(data) => saveIntegration('equifax', data)}
        testIdPrefix="equifax"
      />

      {/* BankStatements.com.au */}
      <IntegrationCard
        name="BankStatements.com.au"
        description="Send secure bank statement requests to clients and auto-upload results to deal documents"
        icon={<BarChart className="h-6 w-6" />}
        color="blue"
        connected={integrations?.bankstatements?.connected}
        fields={[
          { key: 'api_key', label: 'API Key', value: integrations?.bankstatements?.api_key || '' },
          { key: 'partner_id', label: 'Partner ID', value: integrations?.bankstatements?.partner_id || '' }
        ]}
        onSave={(data) => saveIntegration('bankstatements', data)}
        testIdPrefix="bankstatements"
      />
    </div>
  );
}

function IntegrationCard({ name, description, icon, color, connected, fields, onSave, testIdPrefix }) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const v = {};
    fields.forEach(f => { v[f.key] = f.value; });
    setValues(v);
  }, [fields]);

  const handleSave = async () => {
    setSaving(true);
    const hasValues = Object.values(values).some(v => v.trim());
    await onSave({ ...values, connected: hasValues });
    setSaving(false);
    setEditing(false);
  };

  const colorClasses = {
    blue: 'border-blue-500/30 text-blue-400',
    green: 'border-green-500/30 text-green-400'
  };

  return (
    <div className={`bg-navy-800 rounded-xl border ${connected ? colorClasses[color] : 'border-white/5'} p-6`} data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connected ? `bg-${color}-500/20 ${colorClasses[color].split(' ').pop()}` : 'bg-gray-800 text-gray-500'}`}>
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold">{name}</h3>
              {connected ? (
                <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle className="h-3 w-3" /> Connected</span>
              ) : (
                <span className="flex items-center gap-1 text-gray-500 text-xs"><AlertCircle className="h-3 w-3" /> Not Connected</span>
              )}
            </div>
            <p className="text-gray-400 text-sm">{description}</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setEditing(!editing)}
          data-testid={`${testIdPrefix}-configure-btn`}
          className="border-gray-600 text-gray-300 hover:text-white"
        >
          {editing ? 'Cancel' : 'Configure'}
        </Button>
      </div>

      {editing && (
        <div className="space-y-3 pt-4 border-t border-white/5">
          {fields.map(field => (
            <div key={field.key}>
              <label className="text-gray-400 text-sm mb-1 block">{field.label}</label>
              <input
                type="text"
                value={values[field.key] || ''}
                onChange={(e) => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                data-testid={`${testIdPrefix}-${field.key}-input`}
                className="w-full px-4 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                placeholder={`Enter ${field.label}`}
              />
            </div>
          ))}
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`${testIdPrefix}-save-btn`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save {name} Settings
          </Button>
        </div>
      )}
    </div>
  );
}
